// Build-time seed for the tour lists of miami-todo.com — apps/dubai's
// src/lib/prefetch.ts cut down to what this site has: Viator tours in one city.
// No events, no boats table, no guides (no Sanity project yet), so nothing
// here reaches Sanity. Also the collections' near-copy verdict (seedDuplicates).
//
// The pages are prerendered (web.output "static"), and effects don't run
// during the export, so a list fetched in an effect would ship as an empty
// page. Instead this module runs every list query once per export process and
// parks the rows in @todo/core's static-prefetch cache; each page renders its
// cards from it and bakes the same rows into its HTML (seedScript), which
// useTours reads back on the client's first render so hydration matches.
// Called from app/[lang]/_layout.tsx's generateStaticParams.

import type { ToursQuery } from '@todo/core/hooks/use-tours';
import { SUPPORTED_LOCALES } from '@todo/core/i18n/locales';
import { LIST_BUCKET, toursRpcArgs, toursRpcName } from '@todo/core/list-queries';
import { setPrefetched } from '@todo/core/static-prefetch';
import { supabase, type LocaleCopy } from '@todo/core/supabase';
import type { Tour, TourTranslations } from '@todo/core/tours';

import { COLLECTIONS } from '@/lib/collections';
import {
  COLLECTION_DUPLICATES_SEED_KEY,
  COLLECTION_TOURS_LIMIT,
  HOME_CATEGORIES_SEED_KEY,
  collectionBySlug,
  collectionSeedKey,
  findContainedLists,
  findDuplicateLists,
  type CategoryStat,
} from '@todo/web-ui/lib/collections';
import en from '@/lib/i18n/resources/en.json';
import { SITE } from '@site';

const CITY = SITE.cityIds[0];

/** Every tour, best ranked first (tours_by_city's tour_rank_score order). */
export const TOURS_INDEX_QUERY: ToursQuery = { city: CITY, limit: 500 };
export const TOURS_INDEX_SEED_KEY = 'tours-index';

/**
 * The home page's "Featured This Week" row: the best-ranked tours — its own
 * small seed, so the home page doesn't bake the whole catalog to show four
 * cards. No boat card, as dubai's row has: the site has no boats table.
 */
export const FEATURED_CARDS = 4;
export const FEATURED_TOURS_QUERY: ToursQuery = { city: CITY, limit: FEATURED_CARDS };
export const FEATURED_TOURS_SEED_KEY = 'home-tours';

export function collectionQuery(theme: string): ToursQuery {
  return { theme, city: CITY, limit: COLLECTION_TOURS_LIMIT };
}

/**
 * The columns of a tour card, asked of the list RPC with `.select()`, so
 * PostgREST serializes only these, not raw, details and the long copy in
 * three languages. Per locale only the title, as `<locale>_title`.
 */
const CARD_COLUMNS = [
  'id,source,title,city_id,rating,review_count,duration,from_price,currency,image_url',
  ...SUPPORTED_LOCALES.map((locale) => `${locale}_title:translations->${locale}->>title`),
].join(',');

type CardRow = Pick<
  Tour,
  'id' | 'source' | 'title' | 'city_id' | 'rating' | 'review_count' | 'duration' | 'from_price' | 'currency' | 'image_url'
> &
  Record<string, string | null>;

function cardTour(row: CardRow): Tour {
  const translations: Record<string, LocaleCopy | undefined> = {};
  for (const locale of SUPPORTED_LOCALES) {
    const title = row[`${locale}_title`];
    translations[locale] = title ? { title, description: '', summary: '' } : undefined;
  }
  return {
    id: row.id,
    source: row.source,
    source_id: '',
    title: row.title,
    description: null,
    city_id: row.city_id,
    rating: row.rating,
    review_count: row.review_count,
    duration: row.duration,
    from_price: row.from_price,
    currency: row.currency,
    image_url: row.image_url,
    booking_url: null,
    delisted_at: null,
    translations: translations as unknown as TourTranslations,
  };
}

async function fetchTours(query: ToursQuery): Promise<Tour[]> {
  const { data, error } = await supabase.rpc(toursRpcName(query), toursRpcArgs(query)).select(CARD_COLUMNS);
  if (error) throw new Error(`prefetch(${toursRpcName(query)}): ${error.message}`);
  return ((data ?? []) as unknown as CardRow[]).map(cardTour);
}

/** Runs `tasks` at most `limit` at a time, results in task order (as dubai's, against the statement timeout). */
async function pooled<T>(tasks: (() => Promise<T>)[], limit = 4): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) {
      const i = next++;
      results[i] = await tasks[i]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return results;
}

/**
 * Whether a collection has a guide — read from the English copy, written in
 * all three languages at once.
 */
function hasGuide(slug: string): boolean {
  const copy = (en.collections as Record<string, { guide?: unknown[] } | string>)[slug];
  return typeof copy === 'object' && !!copy.guide?.length;
}

/**
 * The build's near-copy verdict over every collection, in COLLECTIONS order
 * (/boats is the first one here — dubai's is a hub page), seeded for the
 * collection pages' `indexable`; the pairs close to it go to the export log.
 */
function seedDuplicates(collections: Tour[][]): void {
  const pages = COLLECTIONS.map((c, i) => ({ slug: c.slug, hasGuide: hasGuide(c.slug), ids: collections[i].map((t) => t.id) }));
  const { duplicateOf, similar } = findDuplicateLists(pages);
  setPrefetched(LIST_BUCKET, COLLECTION_DUPLICATES_SEED_KEY, duplicateOf);
  for (const { slug, other, similarity } of similar) {
    const verdict = duplicateOf[slug] === other ? 'noindex: a copy of' : 'similar to';
    console.log(`[collections] /${slug} ${verdict} /${other} (${Math.round(similarity * 100)}% of their tours shared)`);
  }
  // A page lying inside another (not its parent): fine only while their
  // titles answer different searches — /beach-days and /sandbar-boat-tours.
  for (const { slug, other, shared, count } of findContainedLists(pages, (s) => collectionBySlug(s)?.parent)) {
    console.log(`[collections] /${slug} inside /${other} (${shared} of its ${count} tours) — keep their titles apart`);
  }
}

let prefetchPromise: Promise<void> | null = null;

/** Fetches and seeds every list. Memoized: one round of queries per export. */
export function prefetchLists(): Promise<void> {
  if (!prefetchPromise) {
    prefetchPromise = (async () => {
      const [tours, featuredTours, ...collections] = await pooled([
        () => fetchTours(TOURS_INDEX_QUERY),
        () => fetchTours(FEATURED_TOURS_QUERY),
        ...COLLECTIONS.map((c) => () => fetchTours(collectionQuery(c.theme))),
      ]);
      setPrefetched(LIST_BUCKET, TOURS_INDEX_SEED_KEY, tours);
      setPrefetched(LIST_BUCKET, FEATURED_TOURS_SEED_KEY, featuredTours);
      COLLECTIONS.forEach((c, i) => setPrefetched(LIST_BUCKET, collectionSeedKey(c.slug), collections[i]));
      // The home page's category guide: each category's count and top tour's photo.
      const categoryStats: CategoryStat[] = COLLECTIONS.flatMap((c, i) =>
        c.kind === 'category'
          ? [{ slug: c.slug, count: collections[i].length, image: collections[i][0]?.image_url ?? null }]
          : [],
      );
      setPrefetched(LIST_BUCKET, HOME_CATEGORIES_SEED_KEY, categoryStats);
      seedDuplicates(collections);
    })();
  }
  return prefetchPromise;
}
